import {
  doc,
  getDoc,
  updateDoc,
  deleteDoc,
  arrayRemove,
  serverTimestamp,
  Timestamp
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { OrganisationMemberPrivacySettings } from '../types/organisation';

class OrganisationService {

  async getCoachingGroupIds(userId: string): Promise<string[]> {
    const userDocRef = doc(db, 'organisationMemberships', userId);
    const userDoc = await getDoc(userDocRef);

    // No organisationMemberships doc just means the user isn't in any
    // coaching org - a normal state for most athletes, not an error.
    if (!userDoc.exists()) {
      return [];
    }

    const userData = userDoc.data();
    return userData.coachingGroupIds || [];
  }

  /**
   * Update privacy settings for a member in an organization
   */
  async updateMemberPrivacySettings(
    orgId: string,
    userId: string,
    privacySettings: Partial<OrganisationMemberPrivacySettings>
  ): Promise<void> {
    const memberDocRef = doc(db, 'organisations', orgId, 'members', userId);

    // Check if member exists
    const memberDoc = await getDoc(memberDocRef);
    if (!memberDoc.exists()) {
      throw new Error(`Member ${userId} not found in organization ${orgId}`);
    }

    // Update privacy settings
    await updateDoc(memberDocRef, {
      'privacySettings.wellnessLogs': privacySettings.wellnessLogs ?? true,
      'privacySettings.trainingLogs': privacySettings.trainingLogs ?? true,
      'privacySettings.niggleLogs': privacySettings.niggleLogs ?? true,
      'privacySettings.performanceLogs': privacySettings.performanceLogs ?? true,
      'privacySettings.healthLogs': privacySettings.healthLogs ?? true,
      'privacySettings.noteLogs': privacySettings.noteLogs ?? true,
      'privacySettings.femaleCycleLogs': privacySettings.femaleCycleLogs ?? true,
      'privacySettings.recoveryLogs': privacySettings.recoveryLogs ?? true,
      'privacySettings.shareAllSessions': privacySettings.shareAllSessions ?? true,
      'privacySettings.updatedAt': serverTimestamp(),
    });

    console.log(`Updated privacy settings for user ${userId} in organization ${orgId}`);
  }

  /**
   * Get privacy settings for a member in an organization
   */
  async getMemberPrivacySettings(
    orgId: string,
    userId: string
  ): Promise<OrganisationMemberPrivacySettings> {
    const memberDocRef = doc(db, 'organisations', orgId, 'members', userId);
    const memberDoc = await getDoc(memberDocRef);

    if (!memberDoc.exists()) {
      throw new Error(`Member ${userId} not found in organization ${orgId}`);
    }

    const data = memberDoc.data();

    // Return privacy settings with defaults if not set
    return {
      wellnessLogs: data.privacySettings?.wellnessLogs ?? true,
      trainingLogs: data.privacySettings?.trainingLogs ?? true,
      niggleLogs: data.privacySettings?.niggleLogs ?? true,
      performanceLogs: data.privacySettings?.performanceLogs ?? true,
      healthLogs: data.privacySettings?.healthLogs ?? true,
      noteLogs: data.privacySettings?.noteLogs ?? true,
      femaleCycleLogs: data.privacySettings?.femaleCycleLogs ?? true,
      recoveryLogs: data.privacySettings?.recoveryLogs ?? true,
      shareAllSessions: data.privacySettings?.shareAllSessions ?? true,
      updatedAt: data.privacySettings?.updatedAt?.toDate?.() || undefined,
    };
  }

  /**
   * Initialize default privacy settings for a new member
   * Call this when a member accepts an invitation
   */
  async initializeDefaultPrivacySettings(
    orgId: string,
    userId: string
  ): Promise<void> {
    const memberDocRef = doc(db, 'organisations', orgId, 'members', userId);

    await updateDoc(memberDocRef, {
      'privacySettings.wellnessLogs': true,
      'privacySettings.trainingLogs': true,
      'privacySettings.niggleLogs': true,
      'privacySettings.performanceLogs': true,
      'privacySettings.healthLogs': true,
      'privacySettings.noteLogs': true,
      'privacySettings.femaleCycleLogs': true,
      'privacySettings.recoveryLogs': true,
      'privacySettings.shareAllSessions': true,
      'privacySettings.updatedAt': serverTimestamp(),
    });

    console.log(`Initialized default privacy settings for user ${userId} in organization ${orgId}`);
  }

  /**
   * Remove a member from an organization
   * Deletes the member document and removes the org from the user's coachingGroupIds
   */
  async removeMember(orgId: string, userId: string): Promise<void> {
    // Delete the member document from the organization's members subcollection
    const memberDocRef = doc(db, 'organisations', orgId, 'members', userId);
    await deleteDoc(memberDocRef);
  }

  /**
   * Get the organisation owner's (Coach's) information
   * Returns the coach's ID, first name, and last name
   * @param options - Either { coachId: string } or { orgId: string }
   */
  async getCoach(options: { coachId: string } | { orgId: string }): Promise<{
    coachId: string;
    coachProfilePicture: string;
    coachFirstName: string;
    coachLastName: string;
  }> {
    try {
      let coachId: string;

      // If coachId is provided directly, use it
      if ('coachId' in options) {
        coachId = options.coachId;
        if (!coachId) {
          throw new Error('Coach ID is required');
        }
      }
      // If orgId is provided, fetch the organisation to get the ownerId
      else if ('orgId' in options) {
        const orgId = options.orgId;
        if (!orgId) {
          throw new Error('Organisation ID is required');
        }

        const orgDocRef = doc(db, 'organisations', orgId);
        const orgDoc = await getDoc(orgDocRef);

        if (!orgDoc.exists()) {
          throw new Error(`Organisation ${orgId} not found`);
        }

        const orgData = orgDoc.data();
        coachId = orgData.ownerId;

        if (!coachId) {
          throw new Error(`Organisation ${orgId} has no owner`);
        }
      } else {
        throw new Error('Either coachId or orgId must be provided');
      }

      // Get the coach's user data
      const coachDocRef = doc(db, 'users', coachId);
      const coachDoc = await getDoc(coachDocRef);

      if (!coachDoc.exists()) {
        throw new Error(`Coach user ${coachId} not found`);
      }

      const coachData = coachDoc.data();

      // Get the coach's private data
      const userPrivateDocRef = doc(db, 'users', coachId, 'private', 'data');
      const userPrivateDoc = await getDoc(userPrivateDocRef);

      if (!userPrivateDoc.exists()) {
        throw new Error(`User private data not found for coach ${coachId}`);
      }

      const userData = userPrivateDoc.data();

      return {
        coachId,
        coachProfilePicture: coachData.imageUrl || '',
        coachFirstName: userData.name || '',
        coachLastName: userData.surname || '',
      };
    } catch (error) {
      console.error('Error getting coach:', error);
      throw error;
    }
  }
}

export const organisationService = new OrganisationService();
